import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signOut
} from 'firebase/auth';
import { 
  collection, 
  addDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  serverTimestamp,
  query,
  orderBy 
} from 'firebase/firestore';

import { auth, db } from '../firebase/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // Available collections - using consistent naming
  const collections = ['software', 'aiprojects'];

  // Authentication functions
  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      throw new Error(error.message);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      throw new Error(error.message);
    }
  };

  // Project management functions
  const fetchProjects = async () => {
    try {
      console.log('🔍 Starting to fetch projects...');
      setProjectsLoading(true);
      
      // Fetch from both collections
      const allProjects = [];
      
      for (const collectionName of collections) {
        try {
          console.log(`📚 Fetching from collection: ${collectionName}`);
          const q = query(collection(db, collectionName), orderBy('dateCreated', 'desc'));
          const querySnapshot = await getDocs(q);
          
          console.log(`📄 Found ${querySnapshot.docs.length} documents in ${collectionName}`);
          
          const projectsData = querySnapshot.docs.map(doc => {
            const data = doc.data();
            console.log(`📝 Document ${doc.id}:`, data);
            return {
              id: doc.id,
              collection: collectionName,
              ...data
            };
          });
          
          allProjects.push(...projectsData);
        } catch (error) {
          console.error(`❌ Error fetching from ${collectionName}:`, error);
          
          // If it's a permission error, log it specifically
          if (error.code === 'permission-denied') {
            console.error('🚫 Permission denied - check Firestore security rules');
          }
        }
      }
      
      console.log(`✅ Total projects fetched: ${allProjects.length}`, allProjects);
      
      // Sort all projects by dateCreated
      allProjects.sort((a, b) => {
        const aDate = a.dateCreated?.toDate ? a.dateCreated.toDate() : new Date(a.dateCreated || 0);
        const bDate = b.dateCreated?.toDate ? b.dateCreated.toDate() : new Date(b.dateCreated || 0);
        return bDate - aDate;
      });
      
      setProjects(allProjects);
      return allProjects;
    } catch (error) {
      console.error('💥 Error fetching projects:', error);
      throw new Error('Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  const createProject = async (projectData) => {
    try {
      // Check if user is authenticated for write operations
      if (!currentUser) {
        throw new Error('Authentication required to create projects');
      }

      const { collection: selectedCollection, ...restProjectData } = projectData;
      
      const transformedData = {
        title: projectData.title.trim(),
        description: projectData.description?.trim() || '',
        technologies: projectData.technologies || [],
        features: projectData.features || [],
        // Keep original field names
        githubURL: projectData.githubURL || '',
        websiteURL: projectData.websiteURL || '',
        content: projectData.content || '',
        dateCreated: serverTimestamp(),
      };
      
      console.log('Creating project in collection:', selectedCollection);
      console.log('Project data:', transformedData);
      
      const docRef = await addDoc(collection(db, selectedCollection), transformedData);
      
      // Add the new project to local state
      const newProject = {
        id: docRef.id,
        collection: selectedCollection,
        ...transformedData,
        dateCreated: new Date() // Use current date for immediate display
      };
      
      setProjects(prev => [newProject, ...prev]);
      return newProject;
    } catch (error) {
      console.error('Error creating project:', error);
      console.error('Project data that failed:', projectData);
      throw new Error(`Failed to create project: ${error.message}`);
    }
  };

  const updateProject = async (projectId, projectData) => {
    try {
      // Check if user is authenticated for write operations
      if (!currentUser) {
        throw new Error('Authentication required to update projects');
      }

      // Find the existing project to get its collection
      const existingProject = projects.find(p => p.id === projectId);
      if (!existingProject) {
        throw new Error('Project not found');
      }

      const { collection: selectedCollection, ...restProjectData } = projectData;
      
      const transformedData = {
        title: projectData.title.trim(),
        description: projectData.description?.trim() || '',
        technologies: projectData.technologies || [],
        features: projectData.features || existingProject.features || [],
        // Keep original field names
        githubURL: projectData.githubURL || '',
        websiteURL: projectData.websiteURL || '',
        content: projectData.content || '',
      };

      // If collection changed, we need to delete from old and create in new
      if (selectedCollection !== existingProject.collection) {
        // Delete from old collection
        await deleteDoc(doc(db, existingProject.collection, projectId));
        
        // Create in new collection
        const docRef = await addDoc(collection(db, selectedCollection), {
          ...transformedData,
          dateCreated: existingProject.dateCreated // Preserve original creation date
        });
        
        // Update local state - remove old and add new
        setProjects(prev => 
          prev.map(project => 
            project.id === projectId 
              ? { ...project, id: docRef.id, collection: selectedCollection, ...transformedData }
              : project
          )
        );
        
        return { id: docRef.id, collection: selectedCollection, ...transformedData };
      } else {
        // Same collection, just update
        await updateDoc(doc(db, existingProject.collection, projectId), transformedData);
        
        // Update local state
        setProjects(prev => 
          prev.map(project => 
            project.id === projectId 
              ? { ...project, ...transformedData }
              : project
          )
        );
        
        return { id: projectId, collection: existingProject.collection, ...transformedData };
      }
    } catch (error) {
      console.error('Error updating project:', error);
      throw new Error('Failed to update project');
    }
  };

  const deleteProject = async (projectId) => {
    try {
      // Check if user is authenticated for write operations
      if (!currentUser) {
        throw new Error('Authentication required to delete projects');
      }

      // Find the project to get its collection
      const project = projects.find(p => p.id === projectId);
      if (!project) {
        throw new Error('Project not found');
      }

      await deleteDoc(doc(db, project.collection, projectId));
      
      // Remove from local state
      setProjects(prev => prev.filter(project => project.id !== projectId));
      
      return true;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw new Error('Failed to delete project');
    }
  };

  const getProjectById = (projectId) => {
    return projects.find(project => project.id === projectId);
  };

  const getProjectsByCollection = (collectionName) => {
    return projects.filter(project => project.collection === collectionName);
  };

  const getProjectsByTechnology = (technology) => {
    return projects.filter(project => 
      project.technologies && project.technologies.includes(technology)
    );
  };

  // Fetch projects on component mount (regardless of auth status)
  useEffect(() => {
    fetchProjects().catch(console.error);
  }, []); // Empty dependency array - fetch once on mount

  // Handle authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    // Authentication
    currentUser,
    login,
    logout,
    loading,
    
    // Projects
    projects,
    projectsLoading,
    fetchProjects,
    createProject,
    updateProject,
    deleteProject,
    getProjectById,
    getProjectsByCollection,
    getProjectsByTechnology,
    
    // Collections
    collections,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};